/**
 * Live QA validation, Q-01 .. Q-12 (docs/03 §3.2).
 *
 * This mirrors `backend/app/services/qa.py` rule for rule, so a volunteer
 * working offline sees exactly the feedback they would see online. The server
 * still re-runs every rule on receipt: this copy is for responsiveness, never
 * for authority (golden rule 5).
 *
 * Both copies read their severities and parameters from the `qa_rules`
 * reference data, so retuning a rule on the admin screen changes both.
 */

import type {
  AstValue,
  IsolatePayload,
  QARuleConfig,
  Severity,
  Specimen,
  Organism,
} from "./types";
import { NO_GROWTH_RESULTS } from "./types";

export interface Finding {
  code: string;
  severity: Severity;
  message: string;
  field: keyof IsolatePayload | null;
}

export interface QAContext {
  organismGram: Record<string, string>;
  specimenIsUrine: Record<string, boolean>;
  specimenInpatientTypical: Record<string, boolean>;
  /** Q-10: set by the caller after checking the local queue and the server. */
  duplicateExists?: boolean;
  duplicateRecordId?: string | null;
}

/** Build the lookup maps the rules need from the cached reference data. */
export function buildContext(
  organisms: Organism[],
  specimens: Specimen[],
  extra: Partial<QAContext> = {},
): QAContext {
  return {
    organismGram: Object.fromEntries(organisms.map((o) => [o.code, o.gram])),
    specimenIsUrine: Object.fromEntries(
      specimens.map((s) => [s.code, s.is_urine]),
    ),
    specimenInpatientTypical: Object.fromEntries(
      specimens.map((s) => [s.code, s.is_inpatient_typical]),
    ),
    ...extra,
  };
}

interface RuleSpec {
  severity: Severity;
  definition: Record<string, unknown>;
}

/** Defaults, used when a rule is absent from the reference data. */
export const DEFAULT_RULES: Record<string, RuleSpec> = {
  "Q-01": { severity: "warning", definition: {} },
  "Q-02": { severity: "blocking", definition: {} },
  "Q-03": { severity: "blocking", definition: {} },
  "Q-04": { severity: "blocking", definition: {} },
  "Q-05": { severity: "warning", definition: {} },
  "Q-06": { severity: "blocking", definition: {} },
  "Q-07": { severity: "warning", definition: { drugs: ["VAN", "LNZ", "TEC"] } },
  "Q-08": {
    severity: "warning",
    definition: { drugs: ["AMP", "AMX"], organisms: ["kpn", "kox", "pae", "ecl", "eae"] },
  },
  "Q-09": { severity: "warning", definition: { drugs: ["NIT"] } },
  "Q-10": { severity: "warning", definition: {} },
  "Q-11": {
    severity: "warning",
    definition: { max_years: 120, max_months: 24, max_days: 30 },
  },
  "Q-12": { severity: "warning", definition: {} },
};

/** Explanations behind the "Why?" link beside each warning (docs/08 §8.3). */
export const RULE_EXPLANATIONS: Record<string, string> = {
  "Q-01":
    "The hospital number is how a patient's isolates are linked together in WHONET. Without it the row still counts, but it cannot be de-duplicated.",
  "Q-02": "Every isolate needs a specimen date, or it cannot be placed in a reporting period.",
  "Q-03": "The culture result decides whether the rest of the form applies.",
  "Q-04":
    "If nothing grew there is no organism to name and nothing to test, so these fields must stay empty.",
  "Q-05": "Growth was recorded but no organism was named. Check the register.",
  "Q-06": "Write the organism exactly as the register spells it, so the microbiologist can code it later.",
  "Q-07":
    "Vancomycin, linezolid and teicoplanin have no useful activity against Gram-negative organisms, so a result here is usually a transcription slip.",
  "Q-08":
    "Klebsiella, Pseudomonas and Enterobacter are intrinsically resistant to ampicillin and amoxicillin. A susceptible result is worth double-checking.",
  "Q-09": "Nitrofurantoin only reaches useful concentrations in urine, so it is rarely tested on other specimens.",
  "Q-10": "An isolate with this lab number, date and isolate number already exists. Check you are not entering it twice.",
  "Q-11": "This age is outside the plausible range. Check the units — months and years are easy to mix up.",
  "Q-12": "Blood, CSF and catheter tips almost always come from an inpatient, so the ward is usually known.",
};

/** Antibiotic codes with a real S/I/R value; blank means "not tested" (E-08). */
function filledAst(ast: Record<string, AstValue> | undefined): string[] {
  if (!ast) return [];
  return Object.entries(ast)
    .filter(([, value]) => value === "S" || value === "I" || value === "R")
    .map(([code]) => code);
}

function drugsOf(definition: Record<string, unknown>): string[] {
  const drugs = definition.drugs;
  return Array.isArray(drugs) ? (drugs as string[]) : [];
}

function numberOf(definition: Record<string, unknown>, key: string, fallback: number): number {
  const value = definition[key];
  return typeof value === "number" ? value : fallback;
}

type RuleFn = (
  isolate: Partial<IsolatePayload>,
  ctx: QAContext,
  definition: Record<string, unknown>,
) => string | null;

const RULES: Record<string, { fn: RuleFn; field: keyof IsolatePayload | null }> = {
  "Q-01": {
    field: "hospital_number",
    fn: (iso) => {
      const hasHospital = Boolean((iso.hospital_number ?? "").trim());
      const hasLab = Boolean((iso.lab_number ?? "").trim());
      return hasHospital || hasLab
        ? null
        : "Patient identifier is missing (neither Hospital number nor Lab ID provided).";
    },
  },
  "Q-02": {
    field: "specimen_date",
    fn: (iso) => (iso.specimen_date ? null : "Specimen date is required."),
  },
  "Q-03": {
    field: "culture_result",
    fn: (iso) => (iso.culture_result ? null : "Culture result is required."),
  },
  "Q-04": {
    field: "culture_result",
    fn: (iso) => {
      if (!iso.culture_result || !NO_GROWTH_RESULTS.includes(iso.culture_result)) {
        return null;
      }
      if (iso.organism_code || (iso.organism_as_written ?? "").trim()) {
        return "No growth was recorded, so no organism may be entered.";
      }
      if (filledAst(iso.ast_results).length > 0) {
        return "No growth was recorded, so no antibiotic result may be entered.";
      }
      return null;
    },
  },
  "Q-05": {
    field: "organism_code",
    fn: (iso) =>
      iso.culture_result === "growth" && !iso.organism_code
        ? "Growth was recorded but no organism was named."
        : null,
  },
  "Q-06": {
    field: "organism_as_written",
    fn: (iso) =>
      iso.organism_code === "other" && !(iso.organism_as_written ?? "").trim()
        ? 'Organism is "Other", so the organism as written must be filled in.'
        : null,
  },
  "Q-07": {
    field: "ast_results",
    fn: (iso, ctx, definition) => {
      const organism = iso.organism_code;
      if (!organism || ctx.organismGram[organism] !== "negative") return null;
      const hit = drugsOf(definition).filter((d) => filledAst(iso.ast_results).includes(d));
      return hit.length
        ? `${hit.join(", ")} reported on a Gram-negative organism. These agents have no activity against Gram-negatives.`
        : null;
    },
  },
  "Q-08": {
    field: "ast_results",
    fn: (iso, _ctx, definition) => {
      const organisms = Array.isArray(definition.organisms)
        ? (definition.organisms as string[])
        : [];
      if (!iso.organism_code || !organisms.includes(iso.organism_code)) return null;
      const hit = drugsOf(definition).filter((d) => filledAst(iso.ast_results).includes(d));
      return hit.length
        ? `${hit.join(", ")} reported on an organism that is intrinsically resistant. Check the register.`
        : null;
    },
  },
  "Q-09": {
    field: "ast_results",
    fn: (iso, ctx, definition) => {
      const specimen = iso.specimen_type;
      if (!specimen || ctx.specimenIsUrine[specimen]) return null;
      const hit = drugsOf(definition).filter((d) => filledAst(iso.ast_results).includes(d));
      return hit.length ? `${hit.join(", ")} reported on a non-urine specimen.` : null;
    },
  },
  "Q-10": {
    field: "lab_number",
    fn: (_iso, ctx) =>
      ctx.duplicateExists
        ? `An isolate with this lab number, specimen date and isolate number already exists (${
            ctx.duplicateRecordId ?? "an existing record"
          }).`
        : null,
  },
  "Q-11": {
    field: "age_value",
    fn: (iso, _ctx, definition) => {
      const value = iso.age_value;
      const unit = iso.age_unit;
      if (value === null || value === undefined || !unit) return null;
      const limits: Record<string, number> = {
        years: numberOf(definition, "max_years", 120),
        months: numberOf(definition, "max_months", 24),
        days: numberOf(definition, "max_days", 30),
      };
      const limit = limits[unit];
      return limit !== undefined && value > limit
        ? `Age ${value} ${unit} is outside the plausible range (max ${limit}).`
        : null;
    },
  },
  "Q-12": {
    field: "ward_raw",
    fn: (iso, ctx) => {
      if ((iso.ward_raw ?? "").trim()) return null;
      const specimen = iso.specimen_type;
      return specimen && ctx.specimenInpatientTypical[specimen]
        ? "Ward is blank on a specimen usually taken from an inpatient."
        : null;
    },
  },
};

/** Run every active rule against the form's current values. */
export function evaluate(
  isolate: Partial<IsolatePayload>,
  context: QAContext,
  ruleConfig: QARuleConfig[] = [],
): Finding[] {
  const byCode = new Map(ruleConfig.map((r) => [r.code, r]));
  const findings: Finding[] = [];

  for (const [code, rule] of Object.entries(RULES)) {
    const configured = byCode.get(code);
    if (configured && !configured.is_active) continue;

    const spec = DEFAULT_RULES[code];
    const severity: Severity = configured?.severity ?? spec?.severity ?? "warning";
    const definition = configured?.definition ?? spec?.definition ?? {};

    const message = rule.fn(isolate, context, definition);
    if (message) {
      findings.push({ code, severity, message, field: rule.field });
    }
  }

  return findings;
}

export function blocking(findings: Finding[]): Finding[] {
  return findings.filter((f) => f.severity === "blocking");
}

/** Findings grouped by the field they point at, for inline display. */
export function byField(findings: Finding[]): Map<string, Finding[]> {
  const grouped = new Map<string, Finding[]>();
  for (const finding of findings) {
    const key = finding.field ?? "_form";
    const existing = grouped.get(key);
    if (existing) existing.push(finding);
    else grouped.set(key, [finding]);
  }
  return grouped;
}
